// Admin → Post-Lab Drip tab — Phase 7 tracking + enrollment.
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from "@/components/ui/card";
import { Loader2, RefreshCw, Play, UserPlus, Mail } from "lucide-react";
import { toast } from "sonner";
import { adminFetch, adminPost } from "@/lib/api";

const STEP_COLORS = ["#1A2DD7", "#0EA5E9", "#F59E0B", "#10B981", "#8B5CF6"];

export default function PostLabDrip({ pw }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enrollEmails, setEnrollEmails] = useState("");
  const [enrolling, setEnrolling] = useState(false);
  const [ticking, setTicking] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const d = await adminFetch("/admin/post-lab/status", pw);
      setStatus(d);
    } catch (e) {
      toast.error("Failed to load post-lab status");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const enroll = async () => {
    const emails = enrollEmails.trim();
    if (!emails) { toast.error("Paste at least one email."); return; }
    if (!window.confirm("Enroll these emails into the Post-Lab drip starting at Day 2 (Process Map)?\n\nThey'll get Day 2 in ~2 days, then Day 5, Day 9, Day 13 automatically.")) return;
    setEnrolling(true);
    try {
      const r = await adminPost("/admin/post-lab/enroll-emails", pw, { emails, start_step: 1 });
      toast.success(`Enrolled ${r.newly_enrolled} of ${r.total_unique_emails}. ${r.already_enrolled_skipped} already enrolled, ${r.invalid_skipped?.length || 0} invalid.`);
      setEnrollEmails("");
      load();
    } catch (e) {
      toast.error(e?.message || "Enrollment failed");
    } finally { setEnrolling(false); }
  };

  const runTick = async () => {
    if (!window.confirm("Fire the post-lab scheduler tick right now? Anyone whose next send time has passed will get their next email immediately.")) return;
    setTicking(true);
    try {
      await adminPost("/admin/post-lab/run-now", pw, {});
      toast.success("Tick fired. Check email-usage in a moment.");
      setTimeout(load, 1500);
    } catch (e) {
      toast.error(e?.message || "Tick failed");
    } finally { setTicking(false); }
  };

  const stepCounts = status?.by_step || {};
  const dayLabels = status?.day_labels || {};
  const enrolled = status?.enrolled || [];
  const totalActive = enrolled.filter((r) => r.post_lab_active).length;
  const totalCompleted = enrolled.filter((r) => !r.post_lab_active).length;

  const fmtET = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric", hour: "numeric", minute: "2-digit", hour12: true }); }
    catch { return iso; }
  };

  return (
    <div className="space-y-5" data-testid="post-lab-drip-tab">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="eyebrow mb-1">Phase 7 · Automated 14-Day Drip</div>
          <h2 className="font-display text-2xl tracking-[-0.025em]">Post-Lab Email Sequence</h2>
          <p className="text-sm text-ink/60 mt-1">5 emails over 14 days. Day 0 (photo follow-up) fires at 7 AM ET on the day after the lab. Day 2/5/9/13 auto-follow.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={load} className="btn-outline-ink rounded-full h-10 px-4" data-testid="post-lab-refresh">
            <RefreshCw size={13} className="mr-1.5" />Refresh
          </Button>
          <Button onClick={runTick} disabled={ticking} className="btn-ink rounded-full h-10 px-4" data-testid="post-lab-run-tick">
            {ticking ? <Loader2 size={13} className="animate-spin" /> : <><Play size={13} className="mr-1.5" />Run Tick Now</>}
          </Button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid sm:grid-cols-3 gap-4">
        <Card className="border-[var(--line)] bg-white">
          <CardContent className="pt-5">
            <p className="text-[11px] uppercase tracking-[0.16em] text-ink/55 font-mono mb-1">Active in drip</p>
            <p className="text-3xl font-display tracking-[-0.025em]" data-testid="post-lab-active-count">{loading ? "…" : totalActive}</p>
          </CardContent>
        </Card>
        <Card className="border-[var(--line)] bg-white">
          <CardContent className="pt-5">
            <p className="text-[11px] uppercase tracking-[0.16em] text-ink/55 font-mono mb-1">Completed all 5</p>
            <p className="text-3xl font-display tracking-[-0.025em]" data-testid="post-lab-completed-count">{loading ? "…" : totalCompleted}</p>
          </CardContent>
        </Card>
        <Card className="border-[var(--line)] bg-white">
          <CardContent className="pt-5">
            <p className="text-[11px] uppercase tracking-[0.16em] text-ink/55 font-mono mb-1">Total enrolled</p>
            <p className="text-3xl font-display tracking-[-0.025em]" data-testid="post-lab-total-count">{loading ? "…" : enrolled.length}</p>
          </CardContent>
        </Card>
      </div>

      {/* By step */}
      <Card className="border-[var(--line)] bg-white">
        <CardHeader className="pb-3">
          <CardTitle className="font-display tracking-[-0.025em] text-lg">By step</CardTitle>
          <CardDescription className="text-xs">Active = waiting on next send · Completed = all 5 sent</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {[0, 1, 2, 3, 4].map((step) => {
              const c = stepCounts[step] || stepCounts[String(step)] || { active: 0, completed: 0 };
              return (
                <div key={step} className="rounded-xl border border-[var(--line)] bg-bone p-3" data-testid={`post-lab-step-${step}`}>
                  <div className="flex items-center gap-1.5 mb-2">
                    <span className="w-2 h-2 rounded-full" style={{ background: STEP_COLORS[step] }}></span>
                    <span className="text-[10px] uppercase tracking-[0.14em] text-ink/55 font-mono">{dayLabels[step] || `Step ${step}`}</span>
                  </div>
                  <div className="flex items-baseline gap-3">
                    <span className="text-2xl font-display tracking-[-0.025em]">{c.active}</span>
                    <span className="text-xs text-ink/55">active</span>
                  </div>
                  {c.completed > 0 && <p className="text-xs text-ink/55 mt-1">{c.completed} completed</p>}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Enroll new emails */}
      <Card className="border-[var(--line)] bg-white">
        <CardHeader className="pb-3">
          <CardTitle className="font-display tracking-[-0.025em] text-lg flex items-center gap-2"><UserPlus size={16} className="text-brand" />Enroll attendees into the drip</CardTitle>
          <CardDescription className="text-xs">Paste emails (newline or comma separated). They&rsquo;ll start at Day 2 (Process Map) &mdash; assumes they already received Day 0 via /lab-sender. New emails are added to registrations as <code>manual_attendee</code>.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Textarea
            rows={5}
            value={enrollEmails}
            onChange={(e) => setEnrollEmails(e.target.value)}
            placeholder="attendee@example.com, another@example.com..."
            data-testid="post-lab-enroll-emails"
            className="bg-bone border border-[var(--line)] focus-visible:ring-brand focus-visible:ring-offset-0 font-mono text-sm"
          />
          <Button onClick={enroll} disabled={enrolling} className="btn-ink rounded-full h-10 px-5" data-testid="post-lab-enroll-submit">
            {enrolling ? <Loader2 size={13} className="animate-spin" /> : <><UserPlus size={13} className="mr-1.5" />Enroll into Day 2 onward</>}
          </Button>
        </CardContent>
      </Card>

      {/* Per-registrant table */}
      <Card className="border-[var(--line)] bg-white">
        <CardHeader className="pb-3">
          <CardTitle className="font-display tracking-[-0.025em] text-lg flex items-center gap-2"><Mail size={16} className="text-brand" />Who&rsquo;s at what step</CardTitle>
          <CardDescription className="text-xs">Sorted by next send time (soonest first). Up to 500 rows.</CardDescription>
        </CardHeader>
        <CardContent className="px-0 sm:px-6">
          {loading ? (
            <div className="py-10 text-center text-ink/50"><Loader2 className="inline mr-2 animate-spin" size={14} />Loading…</div>
          ) : enrolled.length === 0 ? (
            <div className="py-10 text-center text-ink/50">No one enrolled yet. Use the form above to enroll attendees.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-[0.16em] text-ink/55 font-mono border-b border-[var(--line)]">
                    <th className="text-left px-3 py-2">Email</th>
                    <th className="text-left px-3 py-2">Step</th>
                    <th className="text-left px-3 py-2">Status</th>
                    <th className="text-left px-3 py-2">Next send (ET)</th>
                    <th className="text-left px-3 py-2">Last sent (ET)</th>
                  </tr>
                </thead>
                <tbody>
                  {enrolled.map((r, i) => {
                    const step = Number(r.post_lab_step ?? 0);
                    const label = dayLabels[step] || `Step ${step}`;
                    return (
                      <tr key={r.email + i} className="border-b border-[var(--line)] hover:bg-bone" data-testid={`post-lab-row-${i}`}>
                        <td className="px-3 py-2 font-mono text-xs">{r.email}</td>
                        <td className="px-3 py-2">
                          <span className="inline-flex items-center gap-1.5 text-xs">
                            <span className="w-1.5 h-1.5 rounded-full" style={{ background: STEP_COLORS[step] || "#999" }}></span>
                            {label}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          {r.unsubscribed ? (
                            <Badge variant="outline" className="border-red-300 text-red-700 bg-red-50">Unsubscribed</Badge>
                          ) : r.post_lab_active ? (
                            <Badge variant="outline" className="border-emerald-300 text-emerald-700 bg-emerald-50">Active</Badge>
                          ) : (
                            <Badge variant="outline" className="border-[var(--line)] text-ink/55 bg-bone">Completed</Badge>
                          )}
                        </td>
                        <td className="px-3 py-2 text-xs text-ink/70">{fmtET(r.post_lab_next_at)}</td>
                        <td className="px-3 py-2 text-xs text-ink/55">{fmtET(r.post_lab_last_sent_at)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
